import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Table,
  Tag,
  Drawer,
  Form,
  Input,
  Select,
  Descriptions,
  Space,
  Divider,
  List,
  Avatar,
  Button,
  message,
  Tooltip
} from 'antd';
import {
  EyeOutlined,
  FacebookFilled,
  WhatsAppOutlined,
  InstagramOutlined,
  MessageOutlined,
  DownCircleOutlined,
  UpCircleOutlined,
  PaperClipOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { getTickets, getTicketsNoLoading, updateTickets, updateTicketsRead } from '../../redux/Tickets/TicketsActions';
import { SocketContext } from '../../context/socketContext';
import { GetOverAllUsers } from '../../redux/Users/UsersActions';
import FileUploader from '../../components/fileUploader';
import ImageAttachmentPreview from '../../components/fileDownloader/ImageAttachmentPreview';
import { MAX_CHAT_FILE_BYTES, MAX_CHAT_FILE_SIZE_LABEL, downloadAttachment, isImageAttachment } from '../../helpers/attachmentUtils';

const { TextArea } = Input;
const { Option } = Select;

const TABLET_MAX = 991;
const PHONE_MAX = 767;

const getTicketDrawerLayout = () => {
  if (typeof window === "undefined") {
    return { width: 720, descriptionColumns: 2 };
  }
  const viewport = window.innerWidth;
  if (viewport <= PHONE_MAX) {
    return { width: "100%", descriptionColumns: 1 };
  }
  if (viewport <= TABLET_MAX) {
    return { width: Math.min(520, viewport - 16), descriptionColumns: 1 };
  }
  return { width: Math.min(720, viewport - 32), descriptionColumns: 2 };
};

const TicketSystem = () => {
  const [form] = Form.useForm();
  const { Socket } = useContext(SocketContext);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setloading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerLayout, setDrawerLayout] = useState(getTicketDrawerLayout);
  const [visible, setVisible] = useState(false);
  const [ticketStatus, setTicketStatus] = useState(null);
  const [updatedTickets, setUpdatedTickets] = useState([]);
  const [allMessages, setAllMessages] = useState([]);
  const [fileList, setFileList] = useState([]);
  const messagesEndRef = useRef(null);
  const [messageApi, contextHolder] = message.useMessage();

  const dispatch = useDispatch();
  const ticketsData = useSelector((state) => state.TicketsReducer.data);
  const isLoadingT = useSelector((state) => state.TicketsReducer.isLoadingT);
  const { overAllUsers } = useSelector((state) => state.Users);
  // const updatedData = useSelector((state) => state.TicketsReducer.updatedData);
  // console.log(updatedData,"updatedData")
  const userData = useSelector((state) => state.Auth.userData);
  const { omniData } = useSelector((state) => state.OmniChannelsReducer);

  useEffect(() => {
    let data = {
      companyId: userData?.companyId,
      id: userData?._id
    }
    dispatch(getTickets(data));
    dispatch(GetOverAllUsers(data));
  }, [])

  useEffect(() => {
    const onResize = () => setDrawerLayout(getTicketDrawerLayout());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (ticketsData?.length > 0) {
      setUpdatedTickets(ticketsData)
    }
  }, [ticketsData]);
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  }, [allMessages]);


  const sourceIcons = {
    facebook: <FacebookFilled style={{ color: '#1877F2' }} />,
    whatsapp: <WhatsAppOutlined style={{ color: '#25D366' }} />,
    instagram: <InstagramOutlined style={{ color: '#E1306C' }} />,
    widget: <MessageOutlined />
  };

  const statusColors = {
    open: 'red',
    'in-progress': 'blue',
    resolved: 'green',
    closed: 'gray'
  };

  const handleReply = (values) => {
    if (selectedTicket?.status === "closed") return;

    const oversizedFile = fileList.find(
      (f) => (f.size ?? f.originFileObj?.size ?? 0) > MAX_CHAT_FILE_BYTES
    );
    if (oversizedFile) {
      messageApi.open({
        type: 'error',
        content: `${oversizedFile.name} exceeds the ${MAX_CHAT_FILE_SIZE_LABEL} size limit`,
      });
      return;
    }

    setloading(true);
    if (selectedTicket?.source === "widget") {
      const newReply = {
        message: {
          type: 'outbound',
          content: values.reply,
          platformMessageId: new Date().toISOString() + selectedTicket?.sender?.platformIds?.widget?.visitorId,
          attachments: fileList,
          timestamp: new Date().toISOString(),
          sendBy: userData?._id,
        },
        subject: selectedTicket?.email?.subject ? selectedTicket?.email?.subject : "Ticket-Support",
        to: selectedTicket?.sender?.platformIds?.widget?.visitorEmail,
        _id: selectedTicket?.sender?._id,
        email: selectedTicket?.email ? selectedTicket?.email : null,
        companyId: userData?.companyId
      };
      Socket.emit("response_email", newReply, (response) => {
        if (response?.updatedTicket) {
          setAllMessages(response?.updatedTicket?.messages);
          setUpdatedTickets(prevTickets => {
            const index = prevTickets.findIndex(ticket => ticket._id === response?.updatedTicket._id);
            if (index !== -1) {
              const updated = [...prevTickets];
              updated[index] = response?.updatedTicket;
              return updated;
            } else {
              return [response?.updatedTicket, ...prevTickets];
            }
          });
        }
        else {
          messageApi.open({
            type: 'error',
            content: 'Api Token not valid or expire else Phone number ID is Incorrect',
          });
        };
        setloading(false);
      })

    }
    else {
      const newReply = {
        message: {
          type: 'outbound',
          content: values.reply,
          timestamp: new Date().toISOString(),
          sendBy: userData?._id,
        },
        source: selectedTicket?.source,
        token: omniData?.access_token,
        number_id: omniData?.phone_number_id,
        to: selectedTicket?.sender?.phone,
        _id: selectedTicket?.sender?._id
      };
      Socket.emit("response_omni", newReply, (response) => {
        if (response?.updatedTicket) {
          setAllMessages(response?.updatedTicket?.messages);
          setUpdatedTickets(prevTickets => {
            const index = prevTickets.findIndex(ticket => ticket._id === response?.updatedTicket._id);
            if (index !== -1) {
              const updated = [...prevTickets];
              updated[index] = response?.updatedTicket;
              return updated;
            } else {
              return [response?.updatedTicket, ...prevTickets];
            }
          });
        }
        else {
          messageApi.open({
            type: 'error',
            content: 'Api Token not valid or expire else Phone number ID is Incorrect',
          });
        };
        setloading(false);
      });
    }
    form.setFieldsValue({
      reply: ''
    });
    setFileList([]);
  };


  const statusFilters = [
    { text: 'Open', value: 'open' },
    { text: 'In Progress', value: 'in-progress' },
    { text: 'Resolved', value: 'resolved' },
    { text: 'Closed', value: 'closed' },
  ];

  const sourceFilters = [
    { text: 'Facebook', value: 'facebook' },
    { text: 'WhatsApp', value: 'whatsapp' },
    { text: 'Instagram', value: 'instagram' },
    { text: 'Widget', value: 'widget' },
  ];

  const columns = [
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: 80,
      align: 'center',
      className: 'tickets-source-col',
      onHeaderCell: () => ({ className: 'tickets-source-col' }),
      onCell: () => ({ className: 'tickets-source-col' }),
      render: (source) => (
        <span className="tickets-source-icon">
          {sourceIcons[source]}
        </span>
      ),
      filters: sourceFilters,
      onFilter: (value, record) => record.source === value,
      filterSearch: true,
    },
    {
      title: 'Customer',
      dataIndex: 'sender',
      key: 'sender',
      render: (text) => <span style={{ maxWidth: 300 }}>{text?.phone}</span>
    },
    {
      title: 'Message Preview',
      dataIndex: 'messages',
      key: 'messages',
      render: (text) => {
        let findLast = text?.length - 1;
        return <span style={{ maxWidth: 300 }}>{text[findLast]?.content?.slice(0, 50)}</span>
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={statusColors[status]}>{status.toUpperCase()}</Tag>
      ),
      filters: statusFilters,
      onFilter: (value, record) => record.status === value,
      filterSearch: true,
    },
    {
      title: 'Received At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => dayjs(date).format('DD/MM/YYYY HH:mm'),
      sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
    },
    {
      title: 'Closed At',
      dataIndex: 'closedAt',
      key: 'closedAt',
      render: (date) => date ? dayjs(date).format('DD/MM/YYYY HH:mm') : "-",
      sorter: (a, b) => dayjs(a.closedAt).unix() - dayjs(b.closedAt).unix(),
    },
  ];

  const onSeletedRow = (record) => {
    setSelectedTicket(record);
    setDrawerVisible(true);
    setAllMessages(record?.messages)
    form.setFieldsValue({
      status: record.status
    });
  };

  const onCloseDrawer = () => {
    if (!selectedTicket?.isRead) {
      dispatch(updateTicketsRead({ _id: selectedTicket?._id, isRead: true })).then((res) => {
        if (res?.status) {
          let data = {
            companyId: userData?.companyId
          }
          dispatch(getTicketsNoLoading(data));
        }
      });
    };
    setDrawerVisible(false);
    setAllMessages([]);
    setSelectedTicket(null);
  };

  useEffect(() => {
    if (!Socket) return;
    if (selectedTicket === null) return;
    Socket?.on("ticketInbound", async (data) => {
      if (selectedTicket?._id === data?._id) {
        setAllMessages(data?.messages);

      }
    });
    return () => { }
  }, [Socket, selectedTicket]);

  useEffect(() => {
    if (selectedTicket === null) return;
    setTicketStatus(selectedTicket?.status)
  }, [selectedTicket]);

  const onSeleteStatus = (e) => {
    if (e !== selectedTicket?.status) {
      setTicketStatus(e);
      setVisible(true)
    }
    else {
      setVisible(false);
      setTicketStatus(e);
    }

  };

  const onSave = () => {
    let data = {
      _id: selectedTicket?._id,
      status: ticketStatus
    };
    dispatch(updateTickets(data)).then((res) => {
      if (res?.status) {
        message.success("Ticket updated successfully!");
        let data = {
          companyId: userData?.companyId
        }
        dispatch(getTicketsNoLoading(data));
        if (selectedTicket?._id === res?.data?._id) {
          setSelectedTicket(res?.data);
        }
      } else {
        message.error("Failed to update ticket.");
      }
    });
  };

  const onRowSelected = (record) => {
    if (!record?.isRead) {
      dispatch(updateTicketsRead({ _id: record?._id, isRead: true })).then((res) => {
        if (res?.status) {
          let data = {
            companyId: userData?.companyId
          }
          dispatch(getTicketsNoLoading(data));
          if (selectedTicket?._id === res?.data?._id) {
            setSelectedTicket(res?.data);
          }
        }
      });
    }
    onSeletedRow(record);
  };


  const getInboundSenderLabel = (ticket) => {
    if (ticket?.source === 'widget') {
      return (
        ticket?.sender?.platformIds?.widget?.visitorEmail ||
        ticket?.sender?.email ||
        ticket?.sender?.phone ||
        'Visitor'
      );
    }
    return ticket?.sender?.phone || 'Customer';
  };

  const downloadTicketAttachment = async (file) => {
    try {
      await downloadAttachment(file, file?.caption);
    } catch (error) {
      console.error("Error downloading the file:", error);
    }
  }


  return (
    <div className="ticketsPage">
      {contextHolder}
      {/* Tickets Table with Built-in Filters */}
      <Table
        loading={isLoadingT}
        columns={columns}
        dataSource={updatedTickets}
        rowKey="id"
        bordered

        onChange={(pagination, filters, sorter) => {
        }}
        onRow={(record) => ({
          onClick: () => onRowSelected(record)
        })}
        rowClassName={(record) => (record.isRead === false ? 'bold-row' : "clickable-row")}
      />

      {/* Ticket Details Drawer */}
      <Drawer
        rootClassName="ticketDetailsDrawer"
        title={`Ticket #${selectedTicket?._id?.slice(-5)}`}
        width={drawerLayout.width}
        open={drawerVisible}
        onClose={() => onCloseDrawer()}
        extra={
          visible && <Button type="primary" onClick={() => onSave()}>Save</Button>
        }
      >
        {selectedTicket && (
          <>
            <Descriptions bordered column={drawerLayout.descriptionColumns} className="ticketDrawer__details">
              <Descriptions.Item label="Customer" span={drawerLayout.descriptionColumns}>
                <Tooltip
                  title={
                    selectedTicket?.source === "whatsapp"
                      ? selectedTicket?.sender?.phone
                      : selectedTicket?.sender?.platformIds?.widget?.visitorEmail
                  }
                >
                  <span className="ticketDrawer__customerValue">
                    {selectedTicket?.source === "whatsapp"
                      ? selectedTicket?.sender?.phone
                      : selectedTicket?.sender?.platformIds?.widget?.visitorEmail}
                  </span>
                </Tooltip>
              </Descriptions.Item>
              {selectedTicket?.source === "widget" &&
                <Descriptions.Item label="Visitor ID">
                  {selectedTicket?.sender?.platformIds?.widget?.visitorId}
                </Descriptions.Item>
              }
              {selectedTicket?.source === "widget" &&
                <Descriptions.Item label="Phone">
                  {selectedTicket?.sender?.phone ? selectedTicket?.sender?.phone : "-"}
                </Descriptions.Item>
              }

              <Descriptions.Item label="Status">
                <Select
                  onChange={(e) => onSeleteStatus(e)}
                  value={ticketStatus}
                  // defaultValue={selectedTicket?.status}
                  disabled={selectedTicket?.status === "closed"}
                  style={{ width: "100%", maxWidth: 160 }}
                >
                  <Option value="open">Open</Option>
                  <Option value="in-progress">In Progress</Option>
                  <Option value="resolved">Resolved</Option>
                  <Option value="closed">Closed</Option>
                </Select>
              </Descriptions.Item>
              <Descriptions.Item label="Source">
                {sourceIcons[selectedTicket.source]}
                {selectedTicket.source.toUpperCase()}
              </Descriptions.Item>
              <Descriptions.Item label="Received">
                {dayjs(selectedTicket.createdAt).format('DD/MM/YYYY HH:mm')}
              </Descriptions.Item>
            </Descriptions>

            <Divider>Conversation</Divider>
            <div ref={messagesEndRef} className="ticketDrawer__messages">
              <List
                dataSource={allMessages}
                renderItem={item => {
                  let findUser = item?.type === "outbound" && overAllUsers?.find((x) => x?._id == item?.sendBy);

                  const inboundLabel = getInboundSenderLabel(selectedTicket);

                  return (
                    <List.Item className="ticketDrawer__messageItem">
                      <List.Item.Meta
                        className="ticketDrawer__messageMeta"
                        avatar={
                          <Avatar
                            icon={item.type === 'outbound' ?
                              <UpCircleOutlined /> : <DownCircleOutlined />}
                            style={{
                              backgroundColor: item.type === 'outbound' ?
                                '#1890ff' : '#ff4d4f'
                            }}
                          />
                        }
                        title={item.type === 'outbound'
                          ? (item?.sendBy === userData?._id ? userData?.sudoName : findUser?.sudoName)
                          : inboundLabel}
                        description={
                          <div style={{ maxWidth: '100%' }}>
                            <div>{dayjs(item.timestamp).format('DD/MM/YYYY HH:mm')}</div>
                            {item?.content != null && item.content !== '' && (
                              <div
                                style={{
                                  marginTop: 8,
                                  wordBreak: 'break-word',
                                  overflowWrap: 'anywhere',
                                  whiteSpace: 'pre-wrap',
                                }}
                              >
                                {item.content}
                              </div>
                            )}
                            {item?.attachments?.length > 0 && (
                              <div style={{ marginTop: 8 }}>
                                {item?.attachments?.map((x) => (
                                  <div key={x?.url || x?.caption} style={{ marginBottom: 8 }}>
                                    {isImageAttachment(x) && <ImageAttachmentPreview file={x} />}
                                    <Tooltip placement="left" title={x?.caption}>
                                      <Button onClick={() => downloadTicketAttachment(x)}>
                                        {/* <PaperClipOutlined /> */}
                                        <DownloadOutlined />
                                      </Button>
                                    </Tooltip>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        }
                      />
                    </List.Item>
                  );
                }}
              />
            </div>

            <Divider>Add Response</Divider>

            <Form form={form} onFinish={handleReply} layout="vertical" className="ticketDrawer__replyForm">
              <Form.Item
                name="reply"
                rules={[{ required: fileList?.length > 0 ? false : true, message: 'Please enter your reply' }]}
              >
                <TextArea disabled={selectedTicket?.status === "closed"} rows={4} placeholder="Type your response..." />
              </Form.Item>
              <Space className="ticketDrawer__actions" wrap>
                <Button disabled={selectedTicket?.status === "closed"} type="primary" htmlType="submit">
                  {loading ? "Please Wait..." : "Send Response"}
                </Button>
                {selectedTicket?.status !== "closed" && selectedTicket?.source === "widget" && !loading && <FileUploader fileList={fileList} setFileList={setFileList} />}
              </Space>
            </Form>
          </>
        )}
      </Drawer>
    </div>
  );
};

export default TicketSystem;